import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import './Login.css';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080';

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing token');
      setVerifying(false);
      return;
    }

    // Verify token on component mount
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/api/submission-reviewers/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        setVerifying(false);
      } else {
        const errorText = await response.text();
        setError(errorText || 'Invalid or expired token');
        setVerifying(false);
      }
    } catch (err) {
      console.error('Token verification error:', err);
      setError('Failed to verify token. Please try again.');
      setVerifying(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validatePassword = () => {
    if (!formData.password) {
      return 'Password is required';
    }
    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${CONFERENCE_API}/api/submission-reviewers/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password: formData.password?.trim()
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const errorText = await response.text();
        setError(errorText || 'Failed to set password');
      }
    } catch (err) {
      console.error('Set password error:', err);
      setError('Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>Verifying Token</h1>
            <p>Please wait while we verify your request</p>
          </div>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner" style={{ margin: '0 auto' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>Invalid Token</h1>
            <p>This link is invalid or has expired</p>
          </div>
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Link to="/login" className="link-primary">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>Success!</h1>
            <p>Your password has been set successfully</p>
          </div>
          <div className="alert alert-success">
            <span>Redirecting to login page...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Set Your Password</h1>
          {userData && (
            <p>Welcome, <strong>{userData.fullName}</strong></p>
          )}
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              minLength="6"
              autoFocus
            />
            <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
              Password must be at least 6 characters
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading || !formData.password || !formData.confirmPassword}
          >
            {loading ? 'Setting Password...' : 'Set Password'}
          </button>
        </form>

        <div style={{ marginTop: '15px', textAlign: 'center' }}>
          <Link to="/login" className="link-primary" style={{ fontSize: '14px' }}>
            Back to Login
          </Link>
        </div>

        <div className="login-footer">
          <p>© 2025 GHA Conference Admin</p>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
